import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import type { WizardDatesValues } from '@/features/reservation/schemas/wizardDates.schema'
import type { OccupantInput } from '@/features/reservation/schemas/wizardOccupants.schema'
import type { WizardOptionsValues } from '@/features/reservation/schemas/wizardUpsells.schema'
import type { WizardPaymentValues } from '@/features/reservation/schemas/wizardPayment.schema'
import type { WizardConfirmationValues } from '@/features/reservation/schemas/wizardConfirmation.schema'
import { isSlotBlockLeaseActive } from '@/features/reservation/utils/slotBlockLease'

export type RoomLineInput = {
  room_type_id: string
  rooms_count: number
}

/**
 * `currentStep` is a zero-based index into the **active** wizard steps for the property
 * (3 steps without options, 4 with options). Confirmation uses `confirmationStepIndex`
 * from `constants/wizardSteps.ts` (index = active step count).
 */
export type ReservationWizardState = {
  propertyId: string | null
  currentStep: number
  dates: WizardDatesValues | null
  occupants: OccupantInput[]
  selectedOptions: WizardOptionsValues | null
  payment: WizardPaymentValues | null
  confirmation: WizardConfirmationValues | null
  /** CGV/CGU acceptance on payment step (brand ui_preferences.require_cgv_consent). */
  cgvAccepted: boolean
  slotBlockExpiresAt: string | null
  /** Slot id the current block-slot lease applies to (persisted with expiry for rehydration). */
  blockedSlotId: string | null
  roomLines: RoomLineInput[]
  setPropertyId: (id: string | null) => void
  setCurrentStep: (step: number) => void
  setDates: (v: WizardDatesValues | null) => void
  setOccupants: (v: OccupantInput[]) => void
  setSelectedOptions: (v: WizardOptionsValues | null) => void
  setPayment: (v: WizardPaymentValues | null) => void
  setConfirmation: (v: WizardConfirmationValues | null) => void
  setCgvAccepted: (v: boolean) => void
  setSlotBlockExpiresAt: (iso: string | null) => void
  setBlockedSlotId: (slotId: string | null) => void
  clearSlotBlockLease: () => void
  setRoomLines: (lines: RoomLineInput[]) => void
  /** Clear stay selection + lease but keep the current property (soft-hold expiry). */
  clearStaySelection: () => void
  reset: () => void
}

const initial = {
  propertyId: null as string | null,
  currentStep: 0,
  dates: null as WizardDatesValues | null,
  occupants: [] as OccupantInput[],
  selectedOptions: null as WizardOptionsValues | null,
  payment: null as WizardPaymentValues | null,
  confirmation: null as WizardConfirmationValues | null,
  cgvAccepted: false,
  slotBlockExpiresAt: null as string | null,
  blockedSlotId: null as string | null,
  roomLines: [] as RoomLineInput[],
}

export const useReservationWizardStore = create<ReservationWizardState>()(
  persist(
    (set) => ({
      ...initial,
      // Changement de logement → repartir d'un wizard propre (évite un
      // `slotBlockExpiresAt`/`currentStep`/`dates` périmés persistés en sessionStorage).
      setPropertyId: (propertyId) =>
        set((s) => {
          if (s.propertyId === propertyId) {
            return s
          }
          return s.propertyId !== null && s.propertyId !== propertyId
            ? { ...initial, propertyId }
            : { propertyId }
        }),
      setCurrentStep: (currentStep) => set({ currentStep }),
      setDates: (dates) => set({ dates }),
      setOccupants: (occupants) => set({ occupants }),
      setSelectedOptions: (selectedOptions) => set({ selectedOptions }),
      setPayment: (payment) => set({ payment }),
      setConfirmation: (confirmation) => set({ confirmation }),
      setCgvAccepted: (cgvAccepted) => set({ cgvAccepted }),
      setSlotBlockExpiresAt: (slotBlockExpiresAt) => set({ slotBlockExpiresAt }),
      setBlockedSlotId: (blockedSlotId) => set({ blockedSlotId }),
      clearSlotBlockLease: () => set({ slotBlockExpiresAt: null, blockedSlotId: null }),
      setRoomLines: (roomLines) => set({ roomLines }),
      clearStaySelection: () =>
        set((s) => ({
          ...initial,
          propertyId: s.propertyId,
          currentStep: 0,
        })),
      reset: () => set({ ...initial }),
    }),
    {
      name: 'reservation-wizard',
      storage: createJSONStorage(() => sessionStorage),
      partialize: (s) => ({
        propertyId: s.propertyId,
        currentStep: s.currentStep,
        dates: s.dates,
        slotBlockExpiresAt: s.slotBlockExpiresAt,
        blockedSlotId: s.blockedSlotId,
        roomLines: s.roomLines,
      }),
      onRehydrateStorage: () => (state) => {
        if (state === undefined) {
          return
        }
        if (state.slotBlockExpiresAt !== null && !isSlotBlockLeaseActive(state.slotBlockExpiresAt)) {
          state.slotBlockExpiresAt = null
          state.blockedSlotId = null
        }
        if (state.slotBlockExpiresAt === null) {
          state.blockedSlotId = null
        }
        if (state.blockedSlotId === null) {
          state.slotBlockExpiresAt = null
        }
      },
    },
  ),
)
